import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ensureLanguageLoaded, SUPPORTED_LANGS, type SupportedLang } from "@/i18n";

type Lang = {
  code: string;
  label: string;
  native_label: string;
  flag: string | null;
  is_active: boolean;
  sort_order: number;
};

const FALLBACK: Lang[] = [
  { code: "cs", label: "Czech", native_label: "Čeština", flag: "🇨🇿", is_active: true, sort_order: 1 },
  { code: "en", label: "English", native_label: "English", flag: "🇬🇧", is_active: true, sort_order: 3 },
  { code: "de", label: "German", native_label: "Deutsch", flag: "🇩🇪", is_active: true, sort_order: 4 },
];

const LanguageSwitcher = ({ compact = false }: { compact?: boolean }) => {
  const { i18n } = useTranslation();
  const [langs, setLangs] = useState<Lang[]>(FALLBACK);

  useEffect(() => {
    let mounted = true;
    supabase
      .from("site_languages")
      .select("*")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        if (mounted && data && data.length > 0) setLangs(data as Lang[]);
      });
    return () => { mounted = false; };
  }, []);

  const current = langs.find((l) => l.code === i18n.language) ||
    langs.find((l) => l.code === "cs") || FALLBACK[0];

  const change = async (code: string) => {
    if (!SUPPORTED_LANGS.includes(code as SupportedLang)) return;
    await ensureLanguageLoaded(code as SupportedLang);
    await i18n.changeLanguage(code);
    localStorage.setItem("app_lang", code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "sm" : "default"}
          className="gap-2 px-2"
          aria-label="Change language"
        >
          <Globe className="h-4 w-4" />
          <span className="text-sm font-medium uppercase">{current.code}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel>Jazyk / Language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {langs.map((l) => {
          const active = l.code === i18n.language;
          return (
            <DropdownMenuItem
              key={l.code}
              onClick={() => change(l.code)}
              className="cursor-pointer flex items-center gap-2"
            >
              <span className="text-lg leading-none">{l.flag}</span>
              <span className="flex-1">{l.native_label}</span>
              {active && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
